import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Mail, Cloud, Building2, RefreshCw, Plus, Trash2, CheckCircle2, Clock, FileSearch } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { ExtractionUploadTab } from "@/components/data/ExtractionUploadTab";

export default function Ingestion() {
  const { familyId, isMFO } = useTenant();
  const [emailAliases, setEmailAliases] = useState([
    { id: '1', alias: 'documents@intake.example.com', family: 'Smith Family', status: 'active' }
  ]);
  const [cloudConnections, setCloudConnections] = useState([
    { id: '1', type: 'Google Drive', folder: '/Family Office/Smith Family', status: 'connected', lastSync: '2 hours ago' }
  ]);

  return (
    <div className="flex-1 space-y-4 p-8">
      
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Data Ingestion</h1>
        <p className="text-muted-foreground">
          Configure automated data imports from multiple sources
        </p>
      </div>

      <Tabs defaultValue="upload" className="space-y-4">
        <TabsList>
          <TabsTrigger value="upload">
            <Upload className="h-4 w-4" />
            Manual Upload
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="h-4 w-4" />
            Email Ingestion
          </TabsTrigger>
          <TabsTrigger value="cloud">
            <Cloud className="h-4 w-4" />
            Cloud Storage
          </TabsTrigger>
          <TabsTrigger value="custodian">
            <Building2 className="h-4 w-4" />
            Custodian APIs
          </TabsTrigger>
        </TabsList>

        {/* Manual Upload with AI Extraction */}
        <TabsContent value="upload" className="space-y-4">
          <ExtractionUploadTab />
        </TabsContent>

        {/* Email Ingestion */}
        <TabsContent value="email" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Intake Aliases</CardTitle>
              <CardDescription>
                Configure per-family email addresses for automated document ingestion
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email Alias</TableHead>
                      <TableHead>Family</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailAliases.map((alias) => (
                      <TableRow key={alias.id}>
                        <TableCell className="font-mono text-sm">{alias.alias}</TableCell>
                        <TableCell>{alias.family}</TableCell>
                        <TableCell>
                          <Badge variant={alias.status === 'active' ? 'default' : 'secondary'}>
                            {alias.status === 'active' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                            {alias.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-medium">Create New Alias</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Alias Name</Label>
                    <Input placeholder="family-name" />
                  </div>
                  {isMFO && (
                    <div className="space-y-2">
                      <Label>Assign to Family</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Select family..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="family1">Smith Family</SelectItem>
                          <SelectItem value="family2">Johnson Family</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <Button>
                  <Plus className="h-4 w-4" />
                  Create Email Alias
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Email Processing Rules</CardTitle>
              <CardDescription>
                Configure how incoming emails are processed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Auto-extract attachments</Label>
                  <p className="text-sm text-muted-foreground">Automatically save email attachments as documents</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Parse statement PDFs</Label>
                  <p className="text-sm text-muted-foreground">Extract transactions and holdings from statements</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Create exceptions for review</Label>
                  <p className="text-sm text-muted-foreground">Flag items that need manual review</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cloud Storage */}
        <TabsContent value="cloud" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cloud Storage Connections</CardTitle>
              <CardDescription>
                Connect to Google Drive or OneDrive for automated document sync
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service</TableHead>
                      <TableHead>Folder Path</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Sync</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cloudConnections.map((conn) => (
                      <TableRow key={conn.id}>
                        <TableCell className="font-medium">{conn.type}</TableCell>
                        <TableCell className="font-mono text-sm">{conn.folder}</TableCell>
                        <TableCell>
                          <Badge variant={conn.status === 'connected' ? 'default' : 'secondary'}>
                            {conn.status === 'connected' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                            {conn.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <Clock className="h-3 w-3 inline mr-1" />
                          {conn.lastSync}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Button variant="outline" className="w-full">
                  <Cloud className="h-4 w-4" />
                  Connect Google Drive
                </Button>
                <Button variant="outline" className="w-full">
                  <Cloud className="h-4 w-4" />
                  Connect OneDrive
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sync Settings</CardTitle>
              <CardDescription>
                Configure automatic sync schedule and preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Sync Frequency</Label>
                <Select defaultValue="hourly">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="realtime">Real-time (when files change)</SelectItem>
                    <SelectItem value="hourly">Every hour</SelectItem>
                    <SelectItem value="daily">Daily at 9 AM</SelectItem>
                    <SelectItem value="manual">Manual only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Only sync new files</Label>
                  <p className="text-sm text-muted-foreground">Skip files that have already been processed</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custodian APIs */}
        <TabsContent value="custodian" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Custodian Integrations</CardTitle>
              <CardDescription>
                Connect directly to custodian APIs for real-time data feeds
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Schwab</p>
                      <p className="text-sm text-muted-foreground">Not connected</p>
                    </div>
                  </div>
                  <Button variant="outline">Connect</Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Fidelity</p>
                      <p className="text-sm text-muted-foreground">Not connected</p>
                    </div>
                  </div>
                  <Button variant="outline">Connect</Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Interactive Brokers</p>
                      <p className="text-sm text-muted-foreground">Not connected</p>
                    </div>
                  </div>
                  <Button variant="outline">Connect</Button>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-3">Custom API Connection</h4>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Custodian Name</Label>
                    <Input placeholder="Enter custodian name" />
                  </div>
                  <div className="space-y-2">
                    <Label>API Endpoint</Label>
                    <Input placeholder="https://api.custodian.com" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <Input type="password" placeholder="Enter API key" />
                    </div>
                    <div className="space-y-2">
                      <Label>API Secret</Label>
                      <Input type="password" placeholder="Enter API secret" />
                    </div>
                  </div>
                  <Button>
                    <Plus className="h-4 w-4" />
                    Add Custom Integration
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
